import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  label?: string;
  showPasswordExternal?: boolean;
  onShowPasswordChange?: (show: boolean) => void;
}

/**
 * 共用密碼輸入欄位元件
 * - 顯示/隱藏密碼切換
 * - Caps Lock 開啟提示 (欄位內 ⇪ 符號)
 */
const PasswordInput = ({
  value,
  onChange,
  placeholder,
  error,
  label,
  showPasswordExternal,
  onShowPasswordChange,
}: PasswordInputProps) => {
  const { t } = useTranslation();
  
  // 內部狀態 (如果沒有外部控制)
  const [showPasswordInternal, setShowPasswordInternal] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);

  // 使用外部或內部狀態
  const showPassword = showPasswordExternal ?? showPasswordInternal;
  const setShowPassword = onShowPasswordChange ?? setShowPasswordInternal;

  const handleKeyEvent = (e: React.KeyboardEvent) => {
    setCapsLockOn(e.getModifierState('CapsLock'));
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium mb-2">{label}</label>
      )}
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyEvent}
          onKeyUp={handleKeyEvent}
          className="w-full px-4 py-3 pr-20 rounded-full border-2 border-foreground bg-background text-foreground placeholder:text-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder={placeholder}
        />
        <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {capsLockOn && (
            <span 
              className="text-foreground/50 text-sm font-bold" 
              title={t("auth.capsLockOn")}
            >
              ⇪
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-foreground/50 hover:text-foreground"
            aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default PasswordInput;
